import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFetch } from "@/lib/api";
import { PageHeader, Loading, ScoreChip } from "@/components/common";
import { fmtUsd, fmtNum } from "@/lib/format";
import { Wallet, Info } from "lucide-react";

const BOARDS = [
  ["early_buyers", "Best Early Buyers"],
  ["consistent", "Most Consistent"],
  ["top_pnl", "Highest Realized PnL"],
  ["risk_adjusted", "Best Risk-Adjusted"],
  ["low_mc_entries", "Top Low-MC Entries"],
  ["most_active", "Most Active"],
  ["narrative_spec", "Top Narrative Specialists"],
  ["most_watched", "Most Watched"],
];

export default function WalletIntelligence() {
  const [board, setBoard] = useState("early_buyers");
  const { data, loading } = useFetch(`/wallets?board=${board}&limit=50`, [board]);
  const nav = useNavigate();
  const isPopularity = board === "most_watched";

  return (
    <div className="fade-in">
      <PageHeader
        icon={Wallet}
        title="Wallet Intelligence"
        subtitle="Smart Wallet Leaderboard. Wallets are evaluated on historical evidence — never automatically called 'good'."
      />

      <div className="mb-4 flex flex-wrap gap-2">
        {BOARDS.map(([v, l]) => (
          <button
            key={v}
            data-testid={`board-${v}`}
            onClick={() => setBoard(v)}
            className={`rounded-lg border px-3 py-1.5 text-xs font-medium ${
              board === v ? "border-amber-600/50 bg-amber-500/10 text-amber-300" : "border-slate-800 bg-panel text-slate-400 hover:border-slate-700"
            }`}
          >
            {l}
          </button>
        ))}
      </div>

      {isPopularity && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-cyan-800/50 bg-cyan-950/20 px-4 py-2.5 text-xs text-cyan-300">
          <Info className="h-4 w-4" /> MOST WATCHED ≠ BEST PERFORMING. Popularity is kept separate from performance.
        </div>
      )}

      {loading || !data ? (
        <Loading />
      ) : (
        <div className="pnut-card overflow-hidden">
          <div className="max-h-[70vh] overflow-auto">
            <table className="pnut-table w-full">
              <thead>
                <tr>
                  <th>#</th><th>Wallet</th><th>Category</th><th>Smart</th><th>Trades</th>
                  <th>Early %</th><th>&lt;$250K</th><th>$1M+</th><th>$5M+</th><th>Realized PnL</th><th>Watchers</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((w, i) => (
                  <tr key={w.address} data-testid={`wallet-row-${i}`} onClick={() => nav(`/wallet/${w.address}`)}>
                    <td className="font-mono text-slate-500">{i + 1}</td>
                    <td className="font-mono text-slate-200">{w.short}</td>
                    <td className="text-slate-400">{w.category}</td>
                    <td><ScoreChip type="momentum" value={w.smart_score} confidence={w.smart_confidence} /></td>
                    <td className="font-mono text-slate-300">{w.analyzable_trades}</td>
                    <td className="font-mono text-amber-400">{w.early_entry_rate}%</td>
                    <td className="font-mono text-slate-300">{w.tokens_below_250k}</td>
                    <td className="font-mono text-emerald-400">{w.reached_1m}</td>
                    <td className="font-mono text-emerald-400">{w.reached_5m}</td>
                    <td className={`font-mono ${w.realized_pnl >= 0 ? "num-pos" : "num-neg"}`}>{fmtUsd(w.realized_pnl)}</td>
                    <td className="font-mono text-cyan-400">{fmtNum(w.watchers_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
